/* src/components/home/headpics.tsx */

"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface HeadpicsProps {
	imageUrls: string[];
}

export function Headpics({ imageUrls }: HeadpicsProps) {
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isHovered, setIsHovered] = useState(false);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	const goToNext = useCallback(() => {
		const isLastSlide = currentIndex === imageUrls.length - 1;
		const newIndex = isLastSlide ? 0 : currentIndex + 1;
		setCurrentIndex(newIndex);
	}, [currentIndex, imageUrls.length]);

	const goToPrevious = useCallback(() => {
		const isFirstSlide = currentIndex === 0;
		const newIndex = isFirstSlide ? imageUrls.length - 1 : currentIndex - 1;
		setCurrentIndex(newIndex);
	}, [currentIndex, imageUrls.length]);

	const goToSlide = (slideIndex: number) => {
		setCurrentIndex(slideIndex);
	};

	useEffect(() => {
		if (isHovered) return;
		timeoutRef.current = setInterval(goToNext, 2100);
		return () => {
			if (timeoutRef.current) clearInterval(timeoutRef.current);
		};
	}, [goToNext, isHovered]);

	return (
		// --- 修正 1: 容器高度响应式调整 ---
		// 移动端 (默认): 使用 aspect-[16/9] 来保持宽高比，高度自适应。
		// 桌面端 (md:): 恢复原有的 calc(100vh - 4rem) 高度。
		<div
			className="w-full h-auto aspect-[16/9] md:h-[calc(100vh-4rem)] md:aspect-auto min-h-[250px] max-h-[600px] relative group"
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<div className="w-full h-full overflow-hidden">
				<div
					className="h-full whitespace-nowrap transition-transform duration-500 ease-in-out"
					style={{ transform: `translateX(-${currentIndex * 100}%)` }}
				>
					{imageUrls.map((url, index) => (
						<div key={index} className="w-full h-full inline-block relative">
							{/* --- 修正 2: 图片 object-fit 响应式调整 --- */}
							{/* 移动端 (默认): object-contain 确保完整图片可见。 */}
							{/* 桌面端 (md:): object-cover 恢复原有的裁剪填充效果。 */}
							<Image
								src={url}
								alt={`Homepage picture ${index + 1}`}
								fill
								priority={index === 0}
								sizes="100vw"
								className="object-contain md:object-cover" // 使用 className 替代 style
							/>
						</div>
					))}
				</div>
			</div>

			{/* --- 修正 3: 左右箭头位置响应式调整 --- */}
			<button
				onClick={goToPrevious}
				className="absolute top-1/2 left-4 md:left-8 -translate-y-1/2 bg-black/30 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 hover:bg-black/50"
			>
				<ChevronLeft size={24} />
			</button>
			<button
				onClick={goToNext}
				className="absolute top-1/2 right-4 md:right-8 -translate-y-1/2 bg-black/30 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 hover:bg-black/50"
			>
				<ChevronRight size={24} />
			</button>

			<div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
				{imageUrls.map((_, slideIndex) => (
					<div
						key={slideIndex}
						onClick={() => goToSlide(slideIndex)}
						className={`w-2 h-2 rounded-full cursor-pointer transition-colors duration-300 ${
							currentIndex === slideIndex ? "bg-white" : "bg-white/50"
						}`}
					/>
				))}
			</div>
		</div>
	);
}
